// Derived metrics for the account-detail page. Pure functions, no React, no
// network — easy to unit-test later. Mirrors the math in the
// Valexos_Pro_V1.038_Dashboard reference HTML so visual parity is exact.

import type { AccountSnapshot } from '@/types/veritas';

export type TradeRow = {
  deal_ticket: number;
  snapshot_ts: string;          // ISO
  symbol: string;
  deal_type: number;            // 0 BUY, 1 SELL
  deal_reason: number;          // 0 CLIENT (manual), 4 EXPERT (bot), …
  magic: number;
  volume: number;
  profit: number;
};

export type EquityPoint = { t: string; balance: number; equity: number | null };

// ─── helpers ──────────────────────────────────────────────────────────────
export const sideOf = (t: TradeRow) => (t.deal_type === 0 ? 'BUY' : 'SELL') as 'BUY' | 'SELL';
export const sourceOf = (t: TradeRow) => (t.magic && t.magic !== 0 ? 'bot' : 'manual') as 'bot' | 'manual';

// ─── headline KPIs ────────────────────────────────────────────────────────
// `totalTrades` is the deal-event count (opens + closes), to match what MT5's
// own History tab shows. `closedPositions` is the count of actual round-trip
// trades — an account that opens and closes 100 positions has 200 deals and
// 100 closed positions, both of which are useful numbers to surface.
// Win rate, profit factor, expectancy, and streaks are all computed against
// closedPositions only (the closing side), so the math reads cleanly:
// winners + losers = closedPositions.
export type Kpis = {
  netPnL: number;
  returnPct: number;
  initialBalance: number;
  finalBalance: number;
  totalTrades: number;            // deal events (opens + closes)
  closedPositions: number;        // closed round-trip count
  winners: number;
  losers: number;
  winRate: number;
  profitFactor: number;
  avgWin: number;
  avgLoss: number;
  expectancy: number;
  totalVolume: number;
  maxDdAbs: number;            // positive number
  maxDdPct: number;            // positive number
  longestWinStreak: number;
  longestLossStreak: number;
};

export function computeKpis(equity: EquityPoint[], trades: TradeRow[]): Kpis {
  if (equity.length === 0 || trades.length === 0) {
    return {
      netPnL: 0, returnPct: 0, initialBalance: 0, finalBalance: 0,
      totalTrades: 0, closedPositions: 0, winners: 0, losers: 0, winRate: 0,
      profitFactor: 0, avgWin: 0, avgLoss: 0, expectancy: 0,
      totalVolume: 0, maxDdAbs: 0, maxDdPct: 0,
      longestWinStreak: 0, longestLossStreak: 0,
    };
  }

  // The `trades` feed is at MT5's DEAL granularity. Each closed position
  // emits TWO rows: an IN deal (entry — profit always 0) and an OUT deal
  // (exit — carries the realized P&L). Computing win-rate as
  // wins/total_deals therefore divides by ~2x the position count and
  // produces nonsense like "38.9% win" out of 349 wins and 94 losses
  // (the actual win rate is 349/(349+94) ≈ 78.8%).
  //
  // Filter once, here, to the "closing" deals — anything with non-zero
  // profit. This is a 99.9% accurate proxy for OUT deals; the only
  // false negative is a true break-even close, which is rare and
  // doesn't move the needle. Better-but-bigger fix: extend TradeRow
  // with deal_entry and check for OUT (DEAL_ENTRY_OUT = 1) directly.
  // Net P&L is summed from the FULL feed because IN deals contribute
  // zero anyway — same number, less computation needed to argue about.
  const closingTrades = trades.filter((t) => t.profit !== 0);

  const initial = equity[0].balance;
  const final = equity[equity.length - 1].balance;
  const winners = closingTrades.filter((t) => t.profit > 0);
  const losers  = closingTrades.filter((t) => t.profit < 0);
  const grossWin  = winners.reduce((a, t) => a + t.profit, 0);
  const grossLoss = Math.abs(losers.reduce((a, t) => a + t.profit, 0));
  const netPnL    = trades.reduce((a, t) => a + t.profit, 0);
  const winRate   = closingTrades.length > 0
    ? (winners.length / closingTrades.length) * 100
    : 0;
  const pf        = grossLoss > 0 ? grossWin / grossLoss : grossWin > 0 ? Infinity : 0;
  const avgWin    = winners.length ? grossWin / winners.length : 0;
  const avgLoss   = losers.length  ? -grossLoss / losers.length : 0;
  const expectancy = (winRate / 100) * avgWin + (1 - winRate / 100) * avgLoss;
  // Volume from closing deals only — IN+OUT volumes match by design,
  // so summing both double-counts. Closing-side gives the actual lots
  // exposed to the market, which is what "lots traded" colloquially means.
  const totalVol  = closingTrades.reduce((a, t) => a + t.volume, 0);

  // Drawdown over the full equity series — snapshot-based, deal feed
  // doesn't matter here.
  let peak = -Infinity, maxDdAbs = 0, maxDdPct = 0;
  for (const p of equity) {
    if (p.balance > peak) peak = p.balance;
    const ddAbs = p.balance - peak;
    const ddPct = peak > 0 ? (ddAbs / peak) * 100 : 0;
    if (ddAbs < maxDdAbs) maxDdAbs = ddAbs;
    if (ddPct < maxDdPct) maxDdPct = ddPct;
  }

  // Streaks — also deal-by-deal, also need the closing-deals filter so
  // a string of IN deals doesn't reset the win streak to zero. Sort
  // chronologically before walking.
  const sorted = [...closingTrades].sort((a, b) => a.snapshot_ts.localeCompare(b.snapshot_ts));
  let curW = 0, curL = 0, lW = 0, lL = 0;
  for (const t of sorted) {
    if (t.profit > 0)      { curW++; lW = Math.max(lW, curW); curL = 0; }
    else if (t.profit < 0) { curL++; lL = Math.max(lL, curL); curW = 0; }
  }

  return {
    netPnL,
    returnPct: ((final - initial) / initial) * 100,
    initialBalance: initial,
    finalBalance: final,
    totalTrades: trades.length,           // deal-event count (matches MT5 History)
    closedPositions: closingTrades.length, // round-trip count (winners + losers)
    winners: winners.length,
    losers: losers.length,
    winRate,
    profitFactor: pf,
    avgWin,
    avgLoss,
    expectancy,
    totalVolume: totalVol,
    maxDdAbs: Math.abs(maxDdAbs),
    maxDdPct: Math.abs(maxDdPct),
    longestWinStreak: lW,
    longestLossStreak: lL,
  };
}

// ─── per-bucket aggregates for the charts ─────────────────────────────────
export function dailyAggregates(trades: TradeRow[]) {
  const map = new Map<string, { date: string; profit: number; trades: number; wins: number; losses: number; volume: number }>();
  for (const t of trades) {
    const date = t.snapshot_ts.slice(0, 10);
    const e = map.get(date) ?? { date, profit: 0, trades: 0, wins: 0, losses: 0, volume: 0 };
    e.profit += t.profit;
    e.trades += 1;
    if (t.profit > 0) e.wins++;
    else if (t.profit < 0) e.losses++;
    e.volume += t.volume;
    map.set(date, e);
  }
  return [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
}

export function hourlyAggregates(trades: TradeRow[]) {
  const buckets = new Map<number, { hour: number; profit: number; trades: number }>();
  for (const t of trades) {
    // Postgres returns timestamptz as full ISO 8601 (e.g.
    // "2026-04-29T11:30:00.123+00:00") — Date can parse it directly.
    // The previous "replace ' ' with 'T' and append 'Z'" pattern was
    // for space-separated input and broke ISO strings (extra Z after the
    // offset → NaN), collapsing every trade into a single bucket.
    const d = new Date(t.snapshot_ts);
    const h = Number.isFinite(d.getTime()) ? d.getUTCHours() : 0;
    const e = buckets.get(h) ?? { hour: h, profit: 0, trades: 0 };
    e.profit += t.profit;
    e.trades += 1;
    buckets.set(h, e);
  }
  return [...buckets.values()].sort((a, b) => a.hour - b.hour);
}

// Win/Loss histogram: bucket P&L into evenly-spaced bins.
export function pnlHistogram(trades: TradeRow[], binCount = 18) {
  if (trades.length === 0) return [];
  const min = Math.min(...trades.map((t) => t.profit));
  const max = Math.max(...trades.map((t) => t.profit));
  const span = Math.max(1, max - min);
  const step = span / binCount;
  const bins = Array.from({ length: binCount }, (_, i) => ({
    lo: min + i * step,
    hi: min + (i + 1) * step,
    count: 0,
    positive: false,
  }));
  for (const t of trades) {
    let idx = Math.floor((t.profit - min) / step);
    if (idx >= binCount) idx = binCount - 1;
    if (idx < 0) idx = 0;
    bins[idx].count++;
  }
  for (const b of bins) b.positive = (b.lo + b.hi) / 2 >= 0;
  return bins.map((b) => ({
    label: `${b.lo.toFixed(0)}…${b.hi.toFixed(0)}`,
    midpoint: (b.lo + b.hi) / 2,
    count: b.count,
    positive: b.positive,
  }));
}

// Position-size buckets. Wide range so a 0.01-lot retail account and a
// 25-lot institutional account both get a useful breakdown — empty
// bands are dropped before render so a small account still sees a
// clean 3-row panel instead of 11 rows of zeros.
//
// Edges are inclusive on the right (so 0.10 lands in 0.06-0.10, not
// in 0.11-0.25). The +1e-9 tolerance forgives float noise on
// brokers that report 0.099999...
export function volumeBuckets(trades: TradeRow[]) {
  const eps = 1e-9;
  const buckets = [
    { label: '0.01',         test: (v: number) => v <= 0.01 + eps },
    { label: '0.02-0.05',    test: (v: number) => v > 0.01 && v <= 0.05 + eps },
    { label: '0.06-0.10',    test: (v: number) => v > 0.05 && v <= 0.10 + eps },
    { label: '0.11-0.25',    test: (v: number) => v > 0.10 && v <= 0.25 + eps },
    { label: '0.26-0.50',    test: (v: number) => v > 0.25 && v <= 0.50 + eps },
    { label: '0.51-1.00',    test: (v: number) => v > 0.50 && v <= 1.00 + eps },
    { label: '1.01-2.50',    test: (v: number) => v > 1.00 && v <= 2.50 + eps },
    { label: '2.51-5.00',    test: (v: number) => v > 2.50 && v <= 5.00 + eps },
    { label: '5.01-10.00',   test: (v: number) => v > 5.00 && v <= 10.00 + eps },
    { label: '10.01-25.00',  test: (v: number) => v > 10.00 && v <= 25.00 + eps },
    { label: '> 25.00',      test: (v: number) => v > 25.00 + eps },
  ].map((b) => ({ ...b, count: 0, profit: 0 }));

  for (const t of trades) {
    for (const b of buckets) {
      if (b.test(t.volume)) { b.count++; b.profit += t.profit; break; }
    }
  }
  const totalTrades = trades.length || 1;
  return buckets
    // Drop empty bands — they're noise. A retail account with all
    // 0.01-0.10 trades shows 3 rows; a futures-style account with
    // 1-5 lot trades shows the rows that matter to it.
    .filter((b) => b.count > 0)
    .map((b) => ({
      label: b.label,
      count: b.count,
      profit: b.profit,
      countShare: b.count / totalTrades,
    }));
}

// Bot vs manual breakdown (count, P&L, win rate, volume).
// Win-rate denominator is closing deals only — same IN-deal-pollution
// fix as computeKpis. count stays at full deal count so the share
// ratio in SourceCard matches the parent "Total Trades" tile.
export function sourceBreakdown(trades: TradeRow[]) {
  const seg = (subset: TradeRow[]) => {
    const closing = subset.filter((t) => t.profit !== 0);
    const wins = closing.filter((t) => t.profit > 0).length;
    return {
      count: subset.length,
      profit: subset.reduce((a, t) => a + t.profit, 0),
      winRate: closing.length ? (wins / closing.length) * 100 : 0,
      volume: closing.reduce((a, t) => a + t.volume, 0),
    };
  };
  return {
    bot:    seg(trades.filter((t) => sourceOf(t) === 'bot')),
    manual: seg(trades.filter((t) => sourceOf(t) === 'manual')),
  };
}

// Build the equity series from the underlying snapshots — keeps the chart
// strictly aligned with the row order in the DB. Includes both `balance`
// (steps on each closed trade) and `equity` (continuous, includes floating
// P&L of any open positions).
export function snapshotsToEquity(rows: AccountSnapshot[]): EquityPoint[] {
  return rows
    .filter((r) => r.balance != null)
    .map((r) => ({
      t: r.snapshot_ts,
      balance: r.balance as number,
      equity: r.equity ?? null,
    }))
    .sort((a, b) => a.t.localeCompare(b.t));
}

// ─── Calendar heatmap aggregates ──────────────────────────────────────────
// Returns a Map<'YYYY-MM-DD', { profit, trades, wins, losses }> built from
// closed-position rows (we use closed_at as the day the trade "counts"
// against). Used by the month-grid heatmap.
export type DayBucket = {
  profit: number;
  trades: number;
  wins: number;
  losses: number;
};

export function dailyBucketsFromPositions(
  positions: Array<{ closed_at: string | null; realized_profit: number }>,
): Map<string, DayBucket> {
  const m = new Map<string, DayBucket>();
  for (const p of positions) {
    if (!p.closed_at) continue;
    const date = p.closed_at.slice(0, 10);
    const e = m.get(date) ?? { profit: 0, trades: 0, wins: 0, losses: 0 };
    e.profit += p.realized_profit;
    e.trades += 1;
    if (p.realized_profit > 0) e.wins++;
    else if (p.realized_profit < 0) e.losses++;
    m.set(date, e);
  }
  return m;
}

// Pick the last N calendar months (ISO 'YYYY-MM' strings, oldest first).
// Used to drive how many month-grids to render in the heatmap.
export function recentMonths(n: number, now: Date = new Date()): string[] {
  const out: string[] = [];
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  for (let i = n - 1; i >= 0; i--) {
    const m = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - i, 1));
    const yy = m.getUTCFullYear();
    const mm = String(m.getUTCMonth() + 1).padStart(2, '0');
    out.push(`${yy}-${mm}`);
  }
  return out;
}

// ─── Cumulative-through-day curve ────────────────────────────────────────
// For each calendar date in the input, build a 24-element curve of
// cumulative P&L at the END of each UTC hour. Then average across all dates.
// The result is a single 24-point series showing the AVERAGE shape of a
// trading day — does the trader build P&L early and give it back, or
// grind through, or only print at close?
export type IntradayPoint = { hour: number; avgCumulative: number; days: number };

export function cumulativeIntradayCurve(
  positions: Array<{ closed_at: string | null; realized_profit: number }>,
): IntradayPoint[] {
  const byDate = new Map<string, Array<{ hour: number; profit: number }>>();
  for (const p of positions) {
    if (!p.closed_at) continue;
    const d = new Date(p.closed_at);
    const ms = d.getTime();
    if (!Number.isFinite(ms)) continue;
    const date = p.closed_at.slice(0, 10);
    const hour = d.getUTCHours();
    const arr = byDate.get(date) ?? [];
    arr.push({ hour, profit: p.realized_profit });
    byDate.set(date, arr);
  }

  if (byDate.size === 0) {
    return Array.from({ length: 24 }, (_, h) => ({ hour: h, avgCumulative: 0, days: 0 }));
  }

  // Build one 24-element cumulative-curve per date
  const curves: number[][] = [];
  for (const trades of byDate.values()) {
    trades.sort((a, b) => a.hour - b.hour);
    const curve = new Array<number>(24).fill(0);
    let cum = 0;
    let h = 0;
    for (const t of trades) {
      // Carry the previous cumulative across any gap hours
      while (h < t.hour) { curve[h] = cum; h++; }
      cum += t.profit;
    }
    while (h < 24) { curve[h] = cum; h++; }
    curves.push(curve);
  }

  // Average across days per hour
  const days = curves.length;
  const out: IntradayPoint[] = [];
  for (let h = 0; h < 24; h++) {
    let sum = 0;
    for (const c of curves) sum += c[h];
    out.push({ hour: h, avgCumulative: sum / days, days });
  }
  return out;
}

// ─── Risk-adjusted metrics ───────────────────────────────────────────────
// All derived from the per-snapshot equity series (account-level, NOT
// source-scoped — these describe actual account performance).
//
// Math notes:
//   * "Daily return" = (last_balance_of_day - last_balance_of_prev_day)
//                      / last_balance_of_prev_day. We collapse multiple
//                      snapshots in a day to the last one.
//   * Sharpe ratio  = mean(daily_returns) / stdev(daily_returns) × √252
//                     (annualised; assumes risk-free rate = 0, which is
//                     the trader convention)
//   * Sortino ratio = mean(daily_returns) / stdev(neg_daily_returns) × √252
//                     (downside-only deviation — penalises losses, not
//                     volatility per se)
//   * Recovery factor = abs(total return %) / max DD %.  Higher = better.
//   * Calmar ratio  = annualised return / max DD %.       Higher = better.
//   * Max DD duration = longest stretch (in calendar days) the equity
//                       spent below a prior peak before reclaiming it.

export type RiskMetrics = {
  sharpe: number | null;
  sortino: number | null;
  recoveryFactor: number | null;
  calmar: number | null;
  maxDdDurationDays: number | null;
  positiveDayPct: number | null;
  totalDays: number;          // trading days only — flat/non-trading days (e.g. weekends) excluded
  flatDaysExcluded: number;   // calendar dates dropped by the flat-day filter (provenance for the UI)
  upDays: number;             // trading days with a positive return
  downDays: number;           // trading days with a negative return
  cumReturnPct: number;
  ongoingDdDays: number;     // days since the most recent peak (0 if at peak)
};

const TRADING_DAYS = 252;
const DAY_MS = 86_400_000;

export function riskMetrics(
  equity: EquityPoint[],
  // Non-trade balance flows ({ t: snapshot_ts, amount }). When supplied, daily
  // balances are deposit-adjusted so a deposit/withdrawal doesn't register as a
  // huge "daily return" — which would otherwise inflate Sharpe/Sortino and
  // distort Calmar/recovery and max-DD. Safe at this DAILY granularity: a date's
  // last balance and the cumulative ops as of that date's END align, so there's
  // no intra-hour mismatch like the rollup series (see L25).
  ops: Array<{ t: string; amount: number }> = [],
): RiskMetrics {
  const empty: RiskMetrics = {
    sharpe: null, sortino: null, recoveryFactor: null, calmar: null,
    maxDdDurationDays: null, positiveDayPct: null,
    totalDays: 0, flatDaysExcluded: 0, upDays: 0, downDays: 0,
    cumReturnPct: 0, ongoingDdDays: 0,
  };
  if (equity.length < 2) return empty;

  // Last balance per calendar date
  const byDate = new Map<string, number>();
  for (const p of equity) {
    if (p.balance == null) continue;
    byDate.set(p.t.slice(0, 10), p.balance);
  }
  const dates = [...byDate.keys()].sort();
  if (dates.length < 2) return empty;

  // Deposit-adjust each date's balance by the cumulative net flows booked through
  // the end of that date, so returns reflect trading only.
  if (ops.length) {
    const sortedOps = [...ops].sort((a, b) => a.t.localeCompare(b.t));
    let i = 0;
    let cum = 0;
    for (const d of dates) {
      while (i < sortedOps.length && sortedOps[i].t.slice(0, 10) <= d) { cum += sortedOps[i].amount; i++; }
      byDate.set(d, (byDate.get(d) as number) - cum);
    }
  }

  // ─── Filter to actual trading days ──────────────────────────────────────
  // A live account's EA pushes snapshots straight through the weekend, so
  // Sat/Sun land in `dates` carrying Friday's balance forward unchanged. Those
  // flat days are neither winning nor losing — counting them inflates the day
  // count AND injects zero-returns that drag Sharpe/Sortino down and push the
  // 252/N annualisation toward a calendar-day rather than a trading-day basis
  // (e.g. 48 calendar dates where only 37 actually traded).
  //
  // Drop any date whose (deposit-adjusted) balance equals the prior RETAINED
  // day's. Because a flat day carries the same balance forward, dropping it
  // preserves the true Fri→Mon return. Runs AFTER deposit-adjustment so a pure
  // deposit/withdrawal day (just zeroed out above) is treated as non-trading
  // rather than a phantom win/loss. Holidays and dormant weekdays fall out the
  // same way, with no calendar/timezone heuristics. maxDdDuration stays
  // calendar-correct because retained dates keep their real date strings.
  const tradingDates: string[] = [];
  for (const d of dates) {
    const bal = byDate.get(d)!;
    const prevRetained = tradingDates.length
      ? byDate.get(tradingDates[tradingDates.length - 1])!
      : null;
    if (prevRetained === null || bal !== prevRetained) tradingDates.push(d);
  }
  if (tradingDates.length < 2) return empty;

  // Daily simple returns (over trading days only)
  const returns: number[] = [];
  for (let i = 1; i < tradingDates.length; i++) {
    const prev = byDate.get(tradingDates[i - 1])!;
    const curr = byDate.get(tradingDates[i])!;
    if (prev > 0) returns.push((curr - prev) / prev);
  }
  if (returns.length === 0) return empty;

  const mean = returns.reduce((a, b) => a + b, 0) / returns.length;
  const variance = returns.reduce((a, r) => a + (r - mean) ** 2, 0) / returns.length;
  const std = Math.sqrt(variance);

  const sharpe = std > 0 ? (mean / std) * Math.sqrt(TRADING_DAYS) : null;

  const negs = returns.filter((r) => r < 0);
  const downStd = negs.length
    ? Math.sqrt(negs.reduce((a, r) => a + r * r, 0) / negs.length)
    : 0;
  const sortino = downStd > 0 ? (mean / downStd) * Math.sqrt(TRADING_DAYS) : null;

  // Max drawdown (% of peak) and longest peak-to-recovery duration in days
  let peak = -Infinity;
  let peakDate: string | null = null;
  let maxDdPct = 0;
  let maxDdDurationDays = 0;
  for (const d of tradingDates) {
    const bal = byDate.get(d)!;
    if (bal >= peak) {
      // Close any open drawdown — duration = peak → this date
      if (peakDate && peakDate !== d) {
        const days = Math.round((Date.parse(d) - Date.parse(peakDate)) / DAY_MS);
        if (days > maxDdDurationDays) maxDdDurationDays = days;
      }
      peak = bal;
      peakDate = d;
    } else {
      const dd = (peak - bal) / peak;
      if (dd > maxDdPct) maxDdPct = dd;
    }
  }
  // If we ended in drawdown, count the ongoing stretch too
  const lastDate = tradingDates[tradingDates.length - 1];
  const lastBal = byDate.get(lastDate)!;
  let ongoingDdDays = 0;
  if (peakDate && lastBal < peak) {
    ongoingDdDays = Math.round((Date.parse(lastDate) - Date.parse(peakDate)) / DAY_MS);
    if (ongoingDdDays > maxDdDurationDays) maxDdDurationDays = ongoingDdDays;
  }

  // Cumulative + annualised return for recovery factor and Calmar
  const initial = byDate.get(tradingDates[0])!;
  const final = byDate.get(lastDate)!;
  const cumReturnPct = initial > 0 ? ((final - initial) / initial) * 100 : 0;
  const annualisedReturn =
    initial > 0 && tradingDates.length >= 2
      ? Math.pow(final / initial, TRADING_DAYS / tradingDates.length) - 1
      : 0;

  const recoveryFactor = maxDdPct > 0 ? (cumReturnPct / 100) / maxDdPct : null;
  const calmar = maxDdPct > 0 ? annualisedReturn / maxDdPct : null;

  const positives = returns.filter((r) => r > 0).length;
  const negatives = returns.filter((r) => r < 0).length;
  const positiveDayPct = (positives / returns.length) * 100;

  return {
    sharpe, sortino, recoveryFactor, calmar,
    maxDdDurationDays: maxDdDurationDays || null,
    positiveDayPct,
    totalDays: tradingDates.length,
    flatDaysExcluded: dates.length - tradingDates.length,
    upDays: positives,
    downDays: negatives,
    cumReturnPct,
    ongoingDdDays,
  };
}

// Per-equity-point drawdown (used by the drawdown chart).
export function drawdownSeries(eq: EquityPoint[]) {
  let peak = -Infinity;
  return eq.map((p) => {
    if (p.balance > peak) peak = p.balance;
    const ddAbs = p.balance - peak;
    const ddPct = peak > 0 ? (ddAbs / peak) * 100 : 0;
    return { t: p.t, ddAbs, ddPct };
  });
}
